import { BaseService, type BaseServiceContext, throwAppError } from '@elasticnote/base-utils';
import { SERVICE_NAME } from '@elasticnote/constants';
import {
  ensureProjectItemIdentities,
  isProjectReadableTextPath,
  projectFilePath,
  relocateProjectItemMetadata,
  removeProjectItemMetadata,
  resolveProjectItemById,
  setProjectItemIdentity,
  type ProjectEntry,
} from '@elasticnote/project-storage';
import {
  WORKSPACE_MAX_DOCUMENT_BYTES,
  buildWorkspaceDeepLink,
  isWorkspacePublicPath,
  isWorkspaceEditableTextPath,
  normalizeWorkspaceAiContext,
  normalizeWorkspaceItemId,
  normalizeWorkspaceNotebookId,
  normalizeWorkspaceRelativePath,
  type WorkspaceAiContext,
  type WorkspaceDocument,
  type WorkspaceItem,
  type WorkspaceNotebook,
  type WorkspaceSearchResult,
  type WorkspaceServiceContract,
} from '@elasticnote/workspace-domain';
import type { WorkspaceInfo } from '@elasticnote/types';
import type { ProjectStorageService } from './project-storage-service';
import type { FileSystemService } from './file-system-service';
import type { WorkspaceOpsService } from './workspace-ops-service';

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: false });
const NOTEBOOK_AI_CONTEXT_KEY = 'elasticnoteAiInstructions';

export type WorkspaceServiceConfig = {
  publicOrigin?: () => string;
};

export type McpNotebookSnapshot = {
  notebook: WorkspaceNotebook;
  documents: Array<Omit<WorkspaceDocument, 'content'> & { content?: string }>;
  folders: WorkspaceItem[];
};

function workspaceProjectId(workspace: WorkspaceInfo): string {
  return normalizeWorkspaceNotebookId(workspace.metadata?.projectId);
}

function workspaceDisplayName(workspace: WorkspaceInfo): string {
  const name = workspace.metadata?.projectDisplayName;
  return typeof name === 'string' && name.trim() ? name.trim() : workspace.name;
}

function workspaceType(workspace: WorkspaceInfo): string {
  const mode = workspace.metadata?.projectModeId;
  return typeof mode === 'string' && mode.trim() ? mode.trim() : 'project';
}

function fileName(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1) || path;
}

function mimeForPath(path: string): string {
  const lower = path.toLowerCase();
  if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'text/markdown';
  if (lower.endsWith('.tex')) return 'text/x-tex';
  if (lower.endsWith('.json')) return 'application/json';
  if (lower.endsWith('.html') || lower.endsWith('.htm')) return 'text/html';
  if (lower.endsWith('.css')) return 'text/css';
  if (lower.endsWith('.js') || lower.endsWith('.mjs') || lower.endsWith('.cjs')) return 'text/javascript';
  if (lower.endsWith('.ts') || lower.endsWith('.tsx')) return 'text/typescript';
  return 'text/plain';
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes.slice().buffer);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

function contentMatchScore(path: string, content: string, query: string): { score: number; snippet: string; line?: number } | undefined {
  const normalizedQuery = query.normalize('NFKC').trim().toLocaleLowerCase();
  if (!normalizedQuery) return undefined;
  const normalizedPath = path.normalize('NFKC').toLocaleLowerCase();
  const normalizedContent = content.normalize('NFKC').toLocaleLowerCase();
  const file = fileName(path).toLocaleLowerCase();
  let offset = normalizedContent.indexOf(normalizedQuery);
  let score = 0;
  if (file === normalizedQuery) score += 1000;
  else if (file.includes(normalizedQuery)) score += 500;
  if (normalizedPath.includes(normalizedQuery)) score += 250;
  if (offset >= 0) score += 100;
  if (!score) return undefined;
  if (offset < 0) return { score, snippet: path };
  const start = Math.max(0, offset - 90);
  const end = Math.min(content.length, offset + normalizedQuery.length + 150);
  const line = normalizedContent.slice(0, offset).split('\n').length;
  return { score, snippet: content.slice(start, end).replace(/\s+/gu, ' ').trim(), line };
}

export class WorkspaceService extends BaseService implements WorkspaceServiceContract {
  static deps = ['workspaceOps', 'projectStorage', 'fileSystem'] as const;

  constructor(
    context: BaseServiceContext,
    private readonly dependencies: {
      workspaceOps: WorkspaceOpsService;
      projectStorage: ProjectStorageService;
      fileSystem: FileSystemService;
    },
    private readonly config: WorkspaceServiceConfig = {},
  ) {
    super(SERVICE_NAME.workspaceService, context, dependencies);
  }

  /**
   * WorkspaceService has no eager startup work. BaseService.mount() still
   * requires every managed service to provide the lifecycle hook, so keep an
   * explicit no-op here rather than relying on TypeScript-only abstract checks.
   */
  hookMount(): void {}

  async listNotebooks(): Promise<WorkspaceNotebook[]> {
    const workspaces = await this.dependencies.workspaceOps.getAllWorkspaces();
    const result: WorkspaceNotebook[] = [];
    for (const workspace of workspaces) {
      const notebookId = workspaceProjectId(workspace);
      if (!notebookId) continue;
      const aiContext = normalizeWorkspaceAiContext(workspace.metadata?.[NOTEBOOK_AI_CONTEXT_KEY]);
      result.push({
        notebookId,
        wsName: workspace.name,
        name: workspaceDisplayName(workspace),
        type: workspaceType(workspace),
        modifiedAt: Number(workspace.lastModified) || 0,
        ...(aiContext ? { aiContext } : {}),
        url: this.deepLink({ notebookId }),
      });
    }
    return result.sort((left, right) => right.modifiedAt - left.modifiedAt || left.name.localeCompare(right.name));
  }

  async resolveNotebook(idOrName: string): Promise<WorkspaceNotebook | undefined> {
    const candidate = String(idOrName || '').trim();
    if (!candidate) return undefined;
    const notebooks = await this.listNotebooks();
    return notebooks.find((notebook) => notebook.notebookId === candidate)
      ?? notebooks.find((notebook) => notebook.wsName === candidate)
      ?? notebooks.find((notebook) => notebook.name.toLocaleLowerCase() === candidate.toLocaleLowerCase());
  }

  async searchWorkspace(query: string, options: { notebookIds?: readonly string[]; limit?: number } = {}): Promise<WorkspaceSearchResult[]> {
    const normalized = String(query || '').trim();
    if (!normalized) return [];
    const allowedIds = options.notebookIds?.length ? new Set(options.notebookIds.map(String)) : undefined;
    const limit = Math.max(1, Math.min(100, Number(options.limit) || 25));
    const notebooks = (await this.listNotebooks()).filter((notebook) => !allowedIds || allowedIds.has(notebook.notebookId));
    const results: WorkspaceSearchResult[] = [];
    let scanned = 0;
    for (const notebook of notebooks) {
      const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
      const entries = await provider.listEntries();
      const metadata = await ensureProjectItemIdentities(provider, entries.map((entry) => ({ path: String(entry.path), kind: entry.kind === 'directory' ? 'folder' : 'file' })));
      for (const entry of entries) {
        if (++scanned > 2500) break;
        const path = String(entry.path);
        if (!isWorkspacePublicPath(path)) continue;
        const itemMetadata = metadata.items[path];
        if (!itemMetadata || itemMetadata.deleted || !normalizeWorkspaceItemId(itemMetadata.itemId)) continue;
        if (entry.kind === 'directory') {
          const matched = contentMatchScore(path, '', normalized);
          if (matched) results.push({
            itemId: itemMetadata.itemId, notebookId: notebook.notebookId, kind: 'folder', path, name: fileName(path),
            modifiedAt: itemMetadata.updatedAt || notebook.modifiedAt, favorite: itemMetadata.favorite, tags: itemMetadata.tags,
            title: fileName(path), snippet: matched.snippet, score: matched.score,
            url: this.deepLink({ notebookId: notebook.notebookId, itemId: itemMetadata.itemId, search: normalized }),
          });
          continue;
        }
        if (!isProjectReadableTextPath(path) || Number(entry.size || 0) > WORKSPACE_MAX_DOCUMENT_BYTES) continue;
        const bytes = await provider.readFile(projectFilePath(path));
        if (!bytes || bytes.byteLength > WORKSPACE_MAX_DOCUMENT_BYTES) continue;
        const content = decoder.decode(bytes);
        const matched = contentMatchScore(path, content, normalized);
        if (!matched) continue;
        results.push({
          itemId: itemMetadata.itemId, notebookId: notebook.notebookId, kind: 'file', path, name: fileName(path),
          modifiedAt: entry.modifiedAt ?? notebook.modifiedAt, favorite: itemMetadata.favorite, tags: itemMetadata.tags,
          title: fileName(path), snippet: matched.snippet, score: matched.score, ...(matched.line ? { line: matched.line } : {}),
          url: this.deepLink({ notebookId: notebook.notebookId, itemId: itemMetadata.itemId, line: matched.line, search: normalized }),
        });
      }
      if (scanned > 2500) break;
    }
    return results.sort((left, right) => right.score - left.score || right.modifiedAt - left.modifiedAt).slice(0, limit);
  }

  async getDocument(input: { notebookId: string; itemId: string }): Promise<WorkspaceDocument> {
    const { notebook, path, entry, metadata } = await this.resolveFile(input.notebookId, input.itemId);
    const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
    const bytes = await provider.readFile(projectFilePath(path));
    if (!bytes) throwAppError('error::workspace:file-not-found', 'Workspace document no longer exists.', { path });
    if (bytes.byteLength > WORKSPACE_MAX_DOCUMENT_BYTES || !isProjectReadableTextPath(path)) {
      throwAppError('error::workspace:document-not-readable', 'Only bounded text documents are available through WorkspaceService.', { path });
    }
    return {
      itemId: metadata.itemId, notebookId: notebook.notebookId, kind: 'file', path, name: fileName(path),
      modifiedAt: entry.modifiedAt ?? notebook.modifiedAt, favorite: metadata.favorite, tags: metadata.tags,
      content: decoder.decode(bytes), contentHash: await sha256(bytes), size: bytes.byteLength, mimeType: mimeForPath(path),
      url: this.deepLink({ notebookId: notebook.notebookId, itemId: metadata.itemId }),
    };
  }

  async createDocument(input: { notebookId: string; path: string; content: string; itemId?: string }): Promise<WorkspaceDocument> {
    const notebook = await this.requireNotebook(input.notebookId);
    const path = normalizeWorkspaceRelativePath(input.path);
    if (!isWorkspacePublicPath(path) || !isWorkspaceEditableTextPath(path)) throw new Error('unsupported_document_path');
    const data = encoder.encode(String(input.content ?? ''));
    if (data.byteLength > WORKSPACE_MAX_DOCUMENT_BYTES) throw new Error('workspace_document_too_large');
    const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
    if ((await provider.listEntries()).some((entry) => String(entry.path) === path)) throw new Error('workspace_document_exists');
    // Route browser-local document creation through FileSystemService so the
    // canonical write emits the same synchronous tree-change notification as
    // every other in-app create. WorkspaceService remains the mutation
    // boundary while ProjectStorage continues to own portable stable identity.
    await this.dependencies.fileSystem.createTextFile(
      `${notebook.wsName}:${path}`,
      String(input.content ?? ''),
    );
    if (input.itemId) await setProjectItemIdentity(provider, path, 'file', input.itemId);
    const metadata = await ensureProjectItemIdentities(provider);
    const identity = metadata.items[path];
    if (!identity) throw new Error('workspace_item_identity_missing');
    return this.getDocument({ notebookId: notebook.notebookId, itemId: identity.itemId });
  }

  async updateDocument(input: { notebookId: string; itemId: string; content: string; baseContentHash: string }): Promise<WorkspaceDocument> {
    const current = await this.getDocument({ notebookId: input.notebookId, itemId: input.itemId });
    if (!input.baseContentHash || current.contentHash !== input.baseContentHash) {
      const error = new Error('workspace_version_conflict');
      Object.assign(error, { expected: input.baseContentHash, actual: current.contentHash });
      throw error;
    }
    const data = encoder.encode(String(input.content ?? ''));
    if (data.byteLength > WORKSPACE_MAX_DOCUMENT_BYTES) throw new Error('workspace_document_too_large');
    const notebook = await this.requireNotebook(input.notebookId);
    const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
    await provider.writeFile(projectFilePath(current.path), data);
    return this.getDocument({ notebookId: notebook.notebookId, itemId: current.itemId });
  }


  async moveDocument(input: { notebookId: string; itemId: string; path: string; baseContentHash: string }): Promise<WorkspaceDocument> {
    const current = await this.getDocument({ notebookId: input.notebookId, itemId: input.itemId });
    if (!input.baseContentHash || current.contentHash !== input.baseContentHash) {
      const error = new Error('workspace_version_conflict');
      Object.assign(error, { expected: input.baseContentHash, actual: current.contentHash });
      throw error;
    }
    const nextPath = normalizeWorkspaceRelativePath(input.path);
    if (!isWorkspacePublicPath(nextPath) || !isWorkspaceEditableTextPath(nextPath)) throw new Error('unsupported_document_path');
    if (nextPath === current.path) return current;
    const notebook = await this.requireNotebook(input.notebookId);
    const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
    const entries = await provider.listEntries();
    if (entries.some((entry) => String(entry.path) === nextPath)) throw new Error('workspace_document_exists');
    const parent = nextPath.includes('/') ? nextPath.slice(0, nextPath.lastIndexOf('/')) : '';
    if (parent && !entries.some((entry) => entry.kind === 'directory' && String(entry.path).replace(/\/$/u, '') === parent)) throw new Error('workspace_parent_folder_not_found');
    await provider.moveFile(projectFilePath(current.path), projectFilePath(nextPath));
    await relocateProjectItemMetadata(provider, current.path, nextPath);
    return this.getDocument({ notebookId: notebook.notebookId, itemId: current.itemId });
  }

  async deleteDocument(input: { notebookId: string; itemId: string; baseContentHash: string }): Promise<{ notebookId: string; itemId: string; path: string }> {
    const current = await this.getDocument({ notebookId: input.notebookId, itemId: input.itemId });
    if (!input.baseContentHash || current.contentHash !== input.baseContentHash) {
      const error = new Error('workspace_version_conflict');
      Object.assign(error, { expected: input.baseContentHash, actual: current.contentHash });
      throw error;
    }
    const notebook = await this.requireNotebook(input.notebookId);
    const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
    await provider.deleteFile(projectFilePath(current.path));
    await removeProjectItemMetadata(provider, current.path, { kind: 'file' });
    return { notebookId: notebook.notebookId, itemId: current.itemId, path: current.path };
  }

  async listFolders(notebookId: string): Promise<WorkspaceItem[]> {
    const notebook = await this.requireNotebook(notebookId);
    const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
    const entries = await provider.listEntries();
    const metadata = await ensureProjectItemIdentities(provider, entries.map((entry) => ({ path: String(entry.path), kind: entry.kind === 'directory' ? 'folder' : 'file' })));
    return entries.filter((entry): entry is Extract<ProjectEntry, { kind: 'directory' }> => entry.kind === 'directory').flatMap((entry) => {
      const path = String(entry.path);
      const item = metadata.items[path];
      if (!item || item.deleted || !isWorkspacePublicPath(path)) return [];
      return [{ itemId: item.itemId, notebookId: notebook.notebookId, kind: 'folder' as const, path, name: fileName(path), modifiedAt: item.updatedAt || notebook.modifiedAt, favorite: item.favorite, tags: item.tags, url: this.deepLink({ notebookId: notebook.notebookId, itemId: item.itemId }) }];
    });
  }

  async getNotebookAiContext(notebookId: string): Promise<WorkspaceAiContext | undefined> {
    const notebook = await this.requireNotebook(notebookId);
    const workspace = await this.dependencies.workspaceOps.getWorkspaceInfo(notebook.wsName);
    const value = normalizeWorkspaceAiContext(workspace?.metadata?.[NOTEBOOK_AI_CONTEXT_KEY]);
    return value?.deleted ? undefined : value;
  }

  async setNotebookAiInstructions(notebookId: string, content: string): Promise<WorkspaceAiContext | undefined> {
    const notebook = await this.requireNotebook(notebookId);
    const normalized = String(content || '').trim();
    const next = normalizeWorkspaceAiContext(normalized ? { content: normalized, updatedAt: Date.now() } : { content: '', deleted: true, updatedAt: Date.now() });
    await this.dependencies.workspaceOps.updateWorkspaceMetadata(notebook.wsName, (metadata) => {
      const result = { ...(metadata ?? {}) } as Record<string, unknown>;
      if (next) result[NOTEBOOK_AI_CONTEXT_KEY] = next;
      return result;
    });
    return next?.deleted ? undefined : next;
  }

  async snapshotNotebookForMcp(notebookId: string): Promise<McpNotebookSnapshot> {
    const notebook = await this.requireNotebook(notebookId);
    const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
    const entries = await provider.listEntries();
    const metadata = await ensureProjectItemIdentities(provider, entries.map((entry) => ({ path: String(entry.path), kind: entry.kind === 'directory' ? 'folder' : 'file' })));
    const documents: McpNotebookSnapshot['documents'] = [];
    const folders: WorkspaceItem[] = [];
    for (const entry of entries) {
      const path = String(entry.path);
      const item = metadata.items[path];
      if (!item || item.deleted || !isWorkspacePublicPath(path)) continue;
      if (entry.kind === 'directory') {
        folders.push({ itemId: item.itemId, notebookId: notebook.notebookId, kind: 'folder', path, name: fileName(path), modifiedAt: item.updatedAt || notebook.modifiedAt, favorite: item.favorite, tags: item.tags, url: this.deepLink({ notebookId: notebook.notebookId, itemId: item.itemId }) });
        continue;
      }
      if (!isProjectReadableTextPath(path) || Number(entry.size || 0) > WORKSPACE_MAX_DOCUMENT_BYTES) continue;
      const bytes = await provider.readFile(projectFilePath(path));
      if (!bytes || bytes.byteLength > WORKSPACE_MAX_DOCUMENT_BYTES) continue;
      documents.push({
        itemId: item.itemId, notebookId: notebook.notebookId, kind: 'file', path, name: fileName(path), modifiedAt: entry.modifiedAt ?? notebook.modifiedAt,
        favorite: item.favorite, tags: item.tags, content: decoder.decode(bytes), contentHash: await sha256(bytes), size: bytes.byteLength, mimeType: mimeForPath(path),
        url: this.deepLink({ notebookId: notebook.notebookId, itemId: item.itemId }),
      });
    }
    return { notebook: { ...notebook, aiContext: await this.getNotebookAiContext(notebook.notebookId) }, documents, folders };
  }

  private async requireNotebook(notebookId: string): Promise<WorkspaceNotebook> {
    const notebook = await this.resolveNotebook(notebookId);
    if (!notebook) throw new Error('workspace_notebook_not_found');
    return notebook;
  }

  private async resolveFile(notebookId: string, itemId: string) {
    const notebook = await this.requireNotebook(notebookId);
    const normalizedItemId = normalizeWorkspaceItemId(itemId);
    if (!normalizedItemId) throw new Error('workspace_item_not_found');
    const provider = await this.dependencies.projectStorage.getProvider(notebook.wsName);
    const resolved = await resolveProjectItemById(provider, normalizedItemId);
    if (!resolved || resolved.entry.kind !== 'file' || resolved.entry.deleted || !isWorkspacePublicPath(resolved.path)) throw new Error('workspace_item_not_found');
    const entry = (await provider.listEntries()).find((candidate) => candidate.kind === 'file' && String(candidate.path) === resolved.path);
    if (!entry || entry.kind !== 'file') throw new Error('workspace_item_not_found');
    return { notebook, path: resolved.path, entry, metadata: resolved.entry };
  }

  private deepLink(input: { notebookId: string; itemId?: string; line?: number; search?: string }): string | undefined {
    const origin = this.config.publicOrigin?.() ?? (typeof window !== 'undefined' ? window.location.origin : '');
    if (!origin) return undefined;
    return buildWorkspaceDeepLink(origin, input);
  }
}
